// @flow

import React from 'react';
import _ from 'lodash';
import bemify from 'bemify-js';
import { connect } from 'react-redux';
import { RouterActions, routeTo } from 'redux-router-kit';

import { DEFAULT_LOGIN_URL } from '@zapier/auth';
import { RouterSelectors } from '@zapier/toolbox-redux-framework';
import { isInIframe } from '@zapier/common-utils';
import { addTruthyParamsToUrl, urlWithNext } from '@zapier/url-utils';

import { ABTest, Profile } from '@zapier/shared-entities';

import Button from 'app/common/components/Button';
import LinkPreserveParamsContainer from 'app/common/components/LinkPreserveParamsContainer';
import LoginActions from 'app/login/LoginActions';
import LoginForm from 'app/login/components/LoginForm';
import MaybeLoad from 'app/common/components/MaybeLoad';
import ResultTypes from 'app/login/ResultTypes';
import Separator from 'app/common/components/Separator';
import TrackActions from 'app/track/TrackActions';
import hocify from 'app/common/components/hocify';
import maybeRender from 'app/common/components/maybeRender';
import { isValidLogin } from 'app/login/submitResults';
import { maybeUnsafeRouteOptions } from 'app/common/utils/RouterUtils';
import {
  CAPTCHA_REQUIRED_FIELD_ERROR,
  PLATFORM_LOGIN_URL,
} from 'app/login/constants';
import {
  SIGNUP_TYPE_EMAIL,
  SIGNUP_TYPE_FACEBOOK,
  SIGNUP_TYPE_GOOGLE,
  SIGNUP_TYPE_MICROSOFT,
} from 'app/track/constants';
import {
  hasLastSessionTimedOut,
  selectCurrentUrl,
  selectSelectedApis,
  selectUseCaseSlug,
} from 'app/login/Selectors';
import 'app/login/components/SignUpFormContainer.scss';
import { useForeverSession } from './useForeverSession';

const bem = bemify('signup-form-container');

const getSignUpPageUrl = isPlatformPage => {
  const urlBase = isPlatformPage ? '/platform' : '';
  return `${urlBase}/sign-up`;
};

const ContinueDescription = ({ email }: { email: string }) => (
  <p>You are currently logged in as {email}.</p>
);

const HelpfulLoggedIn = ({
  next,
  signUpPageUrl,
}: {
  next: string,
  signUpPageUrl: string,
}) => {
  next = urlWithNext(signUpPageUrl, next);
  const logoutUrl = urlWithNext('/logout/', next);
  return (
    <div>
      <p>{<a href={logoutUrl}>Sign up as a different user</a>}</p>
    </div>
  );
};

const HelpfulSignup = ({ href }: { href: string }) => {
  return (
    <div>
      <Separator kind="light">OR</Separator>
      <Button
        className={bem('__signup-link-button')}
        href={href}
        kind="large block important"
        tag="Link"
      >
        Sign Up
      </Button>
    </div>
  );
};

const HelpfulLogin = ({ href, email }: { href: string, email: string }) => {
  return (
    <div>
      <Button
        className={bem('__login-link-button')}
        href={href}
        kind="large block important blue"
        tag="Link"
      >
        Log In as {email}
      </Button>
    </div>
  );
};

const Footer = ({
  isPlatformLogin,
  next,
  shouldRenderLoginLink,
}: {
  isPlatformLogin?: boolean,
  next: ?string,
  shouldRenderLoginLink: boolean,
}) => {
  const loginBaseUrlBase = isPlatformLogin ? '/platform' : '/app';
  const loginUrl = urlWithNext(`${loginBaseUrlBase}/login`, next);

  const signupAgreeNode = (
    <React.Fragment>
      By signing up, you agree to Zapier’s{' '}
      <a href="/terms/" target="_blank">
        Terms of Service
      </a>{' '}
      and{' '}
      <a href="/privacy/" target="_blank">
        Privacy Policy
      </a>
      .
    </React.Fragment>
  );

  return shouldRenderLoginLink ? (
    signupAgreeNode
  ) : (
    <div>
      <p>{signupAgreeNode}</p>
      <p>
        Already have an account?{' '}
        <LinkPreserveParamsContainer href={loginUrl} kind="blue">
          Log In
        </LinkPreserveParamsContainer>
      </p>
    </div>
  );
};

const renderBackFor = (email: string, signUpPageUrl: string) => ({
  onResetFormState,
}: {
  onResetFormState: () => void,
}) => {
  return (
    <p>
      Signing up as {email}.{' '}
      <LinkPreserveParamsContainer
        href={signUpPageUrl}
        kind="blue"
        onClick={onResetFormState}
      >
        Not right?
      </LinkPreserveParamsContainer>
    </p>
  );
};

type SignUpFormContainerMapStateToPropsType = {
  accountName?: string,
  invitationEmail?: string,
  enableGoogleOneTap: boolean,
  isInviting?: boolean,
  signonIdentity?: Object,
};

const mapStateToProps = (
  state,
  props: SignUpFormContainerMapStateToPropsType
) => {
  const page = state.login.signUpPage;
  const currentPathName = RouterSelectors.getCurrentPathname(state);
  const isHomepage = currentPathName === '/';
  const isPlatformPage = currentPathName.includes('/platform/');
  const signUpPageUrl = getSignUpPageUrl(isPlatformPage);

  const selectedApis = selectSelectedApis(state);
  const currentUrl = selectCurrentUrl(state);
  const useCaseSlug = selectUseCaseSlug(state);
  const currency = RouterSelectors.getQueryParam(state, 'currency');

  const next =
    RouterSelectors.getQueryParam(state, 'next') ||
    (isPlatformPage ? PLATFORM_LOGIN_URL : null);

  const shouldDelayEmailOnboarding = RouterSelectors.getQueryParam(
    state,
    'should_delay_email_onboarding'
  );

  const hasGoogleOneTapError = Profile.selectors.all.hasGoogleOneTapError(
    state
  );

  const signonIdentity = props.signonIdentity;
  const hasPreviousSignon = Boolean(signonIdentity?.email);
  const googleSSOButtonText =
    hasPreviousSignon &&
    isHomepage &&
    signonIdentity &&
    signonIdentity.signupType === SIGNUP_TYPE_GOOGLE
      ? `Continue with Google`
      : 'Sign up with Google';
  const facebookSSOButtonText =
    hasPreviousSignon &&
    isHomepage &&
    signonIdentity &&
    signonIdentity.signupType === SIGNUP_TYPE_FACEBOOK
      ? `Continue with Facebook`
      : 'Sign up with Facebook';
  const microsoftSSOButtonText =
    hasPreviousSignon &&
    isHomepage &&
    signonIdentity &&
    signonIdentity.email &&
    signonIdentity.signupType === SIGNUP_TYPE_MICROSOFT
      ? `Continue with Microsoft`
      : 'Sign up with Microsoft';

  const errors = _.get(page.result, 'fieldErrors', {});
  const isCaptchaRequired = _.has(errors, CAPTCHA_REQUIRED_FIELD_ERROR);

  const fieldErrorsCount = Object.keys(errors).length;
  const hasOnlyPasswordError = !!errors.password1 && fieldErrorsCount === 1;
  const shouldShowPasswordField =
    page.touchedFields.password1 || hasOnlyPasswordError;
  const shouldShowSignUpFields = !shouldShowPasswordField;

  let shouldShowLoginForm = true;

  if (isHomepage && hasPreviousSignon) {
    shouldShowLoginForm = false;
  }

  let googleSSOEnabled = shouldShowSignUpFields && !isInIframe();
  let facebookSSOEnabled = shouldShowSignUpFields;
  let microsoftSSOEnabled = shouldShowSignUpFields && !isInIframe();
  const showLoginEmailRedirectLink =
    isHomepage && signonIdentity?.signupType === SIGNUP_TYPE_EMAIL;

  /*
    Returning user logic here - these cases cover if a user was logged out due to an expired session,
    if they were we need to enable/disable certain elements of the login form, which we only do on the
    home page.
  */

  // If the user signed up with either email or Facebook SSO, we should disable Google SSO and Microsoft SSO.
  if (
    isHomepage &&
    (signonIdentity?.signupType === SIGNUP_TYPE_EMAIL ||
      signonIdentity?.signupType === SIGNUP_TYPE_FACEBOOK)
  ) {
    googleSSOEnabled = false;
    microsoftSSOEnabled = false;
  }

  // If the user signed up with either Google SSO or email, we should disable Facebook SSO and Microsoft SSO.
  if (
    isHomepage &&
    (signonIdentity?.signupType === SIGNUP_TYPE_EMAIL ||
      signonIdentity?.signupType === SIGNUP_TYPE_GOOGLE)
  ) {
    facebookSSOEnabled = false;
    microsoftSSOEnabled = false;
  }

  // If the user signed up with either Microsoft SSO or email, we should disable Facebook SSO and Google SSO.
  if (
    isHomepage &&
    (signonIdentity?.signupType === SIGNUP_TYPE_EMAIL ||
      signonIdentity?.signupType === SIGNUP_TYPE_MICROSOFT)
  ) {
    facebookSSOEnabled = false;
    googleSSOEnabled = false;
  }

  const welcomeMessage = hasPreviousSignon && isHomepage ? 'Welcome Back!' : '';

  const msg = (() => {
    if (hasGoogleOneTapError) {
      return {
        text: 'Oops, looks like we had an error creating your account!',
        type: 'error',
      };
    }

    return state.errors.length > 0
      ? { text: state.errors[state.errors.length - 1].message, type: 'error' }
      : {};
  })();

  const currentAccountEmail = Profile.selectors.email(
    Profile.selectors.all.currentProfile(state)
  );

  const isLoggedIn =
    !!currentAccountEmail &&
    !hasLastSessionTimedOut(state) &&
    !page.isSubmitting &&
    page.result !== ResultTypes.IS_VALID_LOGIN;

  const signUpEmail = props.isInviting
    ? props.invitationEmail
    : page.form.email;

  const canSubmit = isLoggedIn || !page.isSubmitting;

  const fields = isLoggedIn
    ? undefined
    : _.compact([
        {
          autoFocus: true,
          name: Profile.EMAIL,
          type: shouldShowSignUpFields ? 'email' : 'hidden',
          placeholder: 'Work Email',
          required: true,
          value: signUpEmail,
          disabled: props.isInviting,
        },
        shouldShowSignUpFields &&
          errors.email && {
            type: 'error',
            name: 'email',
            message: errors.email,
          },
        {
          name: Profile.FIRST_NAME,
          type: shouldShowSignUpFields ? 'text' : 'hidden',
          placeholder: 'First Name',
          required: true,
          autoFocus: false,
          value: page.form.first_name,
          classes: ['--left'],
          hideUntilInteraction: true,
        },
        {
          name: Profile.LAST_NAME,
          type: shouldShowSignUpFields ? 'text' : 'hidden',
          placeholder: 'Last Name',
          required: true,
          value: page.form.last_name,
          classes: ['--right'],
          hideUntilInteraction: true,
        },
        shouldShowSignUpFields &&
          errors.first_name && {
            type: 'error',
            name: 'first_name',
            message: errors.first_name,
            classes: ['--left'],
          },
        shouldShowSignUpFields &&
          errors.last_name && {
            type: 'error',
            name: 'last_name',
            message: errors.last_name,
            classes: ['--right'],
          },
        {
          autoFocus: shouldShowPasswordField,
          label: 'Password',
          name: Profile.NEW_PASSWORD,
          type: shouldShowPasswordField ? 'password' : 'hidden',
          placeholder: 'Password',
          required: true,
          value: page.form.password1,
          hideUntilInteraction: false,
        },
        {
          name: 'should_delay_email_onboarding',
          type: 'hidden',
          value: shouldDelayEmailOnboarding || isPlatformPage,
        },
        shouldShowPasswordField &&
          errors.password1 &&
          page.submittedFields.password1 && {
            type: 'error',
            name: 'password1',
            message: errors.password1,
          },
      ]);

  const title = props.accountName
    ? `Sign up to join ${props.accountName} on Zapier`
    : 'Sign up to create your first Zap';

  const shouldShowGoogleOneTap = true;

  let footer = null;

  if (!isLoggedIn) {
    footer = (
      <Footer
        isPlatformLogin={isPlatformPage}
        next={next}
        shouldRenderLoginLink={isHomepage}
      />
    );
  }

  if (isHomepage && hasPreviousSignon) {
    footer = (
      <HelpfulSignup
        href={addTruthyParamsToUrl(
          signUpPageUrl,
          RouterSelectors.getQueryParams(state)
        )}
      />
    );
  }

  return {
    ...props,
    selectedApis,
    currentUrl,
    useCaseSlug,
    currency,
    title,
    submitText: isLoggedIn ? `Continue as ${currentAccountEmail}` : 'Sign Up',
    canSubmit,
    welcomeMessage,
    didSucceed: isValidLogin(page.result),
    isSubmitting: page.isSubmitting,
    fields,
    isCaptchaRequired,
    googleSSOButtonText,
    facebookSSOButtonText,
    microsoftSSOButtonText,
    message: msg.text,
    messageType: msg.type,
    description: isLoggedIn ? (
      <ContinueDescription email={currentAccountEmail} next={next} />
    ) : shouldShowPasswordField && signUpEmail ? (
      renderBackFor(signUpEmail, signUpPageUrl)
    ) : null,
    helpfulMessage: isLoggedIn && (
      <HelpfulLoggedIn
        email={currentAccountEmail}
        next={next || DEFAULT_LOGIN_URL}
        signUpPageUrl={signUpPageUrl}
      />
    ),
    footer,
    // Respect `next` coming from a `?next=` query param, but if it doesn't exist,
    // use a default based on whether the user is already logged in or not
    next: next || (isLoggedIn ? DEFAULT_LOGIN_URL : ''),
    shouldDelayEmailOnboarding,
    googleSSOEnabled,
    facebookSSOEnabled,
    microsoftSSOEnabled,
    shouldShowLoginForm,
    shouldShowPasswordField,
    hasGoogleOneTapError,
    loginHelperButton:
      signonIdentity?.email && showLoginEmailRedirectLink ? (
        <HelpfulLogin
          email={signonIdentity?.email || ''}
          href={DEFAULT_LOGIN_URL}
        />
      ) : null,
    shouldAutologin:
      isHomepage && signonIdentity?.signupType === SIGNUP_TYPE_GOOGLE,
    // For google one-tap, we respect `next` coming from a `?next=` query param, but we can't have a static default `next`, because
    // for google one-tap, the default `next` is dynamic (if the user signed up via one-tap but an account already existed with that
    // e-mail address, or if it's a completely new account)
    googleOneTapNext: next || null,
    // Google One Tap is currently implemented to 100% of users on the homepage
    // so this allows us to provide a prop to avoid having to check the result
    // of an AB Test to show / hide Google One Tap
    disableGoogleOneTap: !props.enableGoogleOneTap && !shouldShowGoogleOneTap,
    // Rendering should only be delayed if we need to see if the user is
    // eligible for google one tap
    shouldRender:
      props.enableGoogleOneTap || ABTest.selectors.all.hasLoaded(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    onLoad() {
      dispatch(ABTest.actions.loadCollection());
    },
    onChange: (value, name) => {
      dispatch(LoginActions.updateForm('signUpPage', name, value));
    },
    onRedirectToSignup: () => {
      dispatch(RouterActions.routeTo('/sign-up'));
    },
    onResetFormState: () => {
      dispatch(LoginActions.resetForm('signUpPage'));
      // Reset the status of the Google One Tap entity when we reset the
      // form state. This will ensure that errors don't carry over when
      // a user navigates back to a signup form.
      dispatch(
        Profile.actions.resetEntityStatus(Profile.GOOGLE_ONE_TAP_NEW_ENTITY_ID)
      );
    },
    onSubmit: (fields, next, captchaValue) => {
      if (!fields) {
        dispatch(routeTo(next, maybeUnsafeRouteOptions()));
        return;
      }
      const signUpData = fields.reduce((data, field) => {
        if (field.value !== undefined) {
          data[field.name] = field.value;
        }
        return data;
      }, {});

      signUpData.captcha = captchaValue;
      dispatch(LoginActions.signUp(signUpData, next));
    },
    onGoogleOneTapSubmit(idToken, googleOneTapNext) {
      dispatch(
        LoginActions.attemptOneTapGoogleSignup(idToken, googleOneTapNext)
      );
    },
    track: (message, data = null) => {
      dispatch(TrackActions.track(message, data));
    },
  };
};

function SignupFormFormContainerWrapper(
  props: SignUpFormContainerMapStateToPropsType
) {
  const { isLoading, foreverSession } = useForeverSession();

  return isLoading ? null : (
    <SignUpFormContainer
      {...props}
      signonIdentity={foreverSession?.signonidentity}
    />
  );
}

const SignUpFormContainer = _.flowRight(
  connect(mapStateToProps, mapDispatchToProps),
  hocify(MaybeLoad),
  maybeRender(props => props.shouldRender)
)(LoginForm);

SignUpFormContainer.displayName = 'SignUpFormContainer';

export default SignupFormFormContainerWrapper;
